import { queryClient } from "@modules/shared/Providers/queryClient";
import { QueryClient } from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { createIsomorphicFn } from "@tanstack/react-start";
import { LazyMotion } from "framer-motion";
import { Provider, createStore } from "jotai";
import { StrictMode, Suspense, lazy } from "react";
import { getCookie } from "@utils/cookies";
import { USER_ACCESS_TOKEN_KEY } from "../consts/keys";
import { routeTree } from "./routeTree.gen";
import { Providers } from "@/modules/shared/Providers/Providers";
import { NotFound } from "@/defaults/NotFound";
import { DefaultCatchBoundary } from "@/defaults/DefaultCatchBoundary";

const DynamicToaster = lazy(() =>
  import("@ui/components/ui/sonner").then(module => ({
    default: module.Toaster,
  }))
);

// NOTE: Most of the integration code found here is experimental and will
// definitely end up in a more streamlined API in the future. This is just
// to show what's possible with the current APIs.

export const getRouter = createIsomorphicFn()
  .server(() => {
    // create server side router
    const tempQueryClient = new QueryClient();

    const router = createTanStackRouter({
      routeTree,
      context: {
        queryClient: tempQueryClient,
        isAuthenticated: () => {
          return Boolean(getCookie(USER_ACCESS_TOKEN_KEY));
        },
      },
      defaultPreload: "intent",
      defaultErrorComponent: DefaultCatchBoundary,
      defaultNotFoundComponent: () => <NotFound />,
      // Pages scroll inside <main>, not the window. Start every page at the top,
      // including on back and forward.
      scrollToTopSelectors: ["main"],
      InnerWrap: ({ children }) => <Providers>{children}</Providers>,
    });

    setupRouterSsrQueryIntegration({ router, queryClient: tempQueryClient });

    return router;
  })
  .client(() => {
    // create client side router

    const tempQueryClient = queryClient;
    const store = createStore();

    const router = createTanStackRouter({
      routeTree,
      context: {
        queryClient: tempQueryClient,
        isAuthenticated: () => {
          return Boolean(getCookie(USER_ACCESS_TOKEN_KEY));
        },
      },
      defaultPreload: "intent",
      defaultErrorComponent: DefaultCatchBoundary,
      defaultNotFoundComponent: () => <NotFound />,
      defaultViewTransition: true,
      // Pages scroll inside <main>, not the window. Start every page at the top,
      // including on back and forward.
      scrollToTopSelectors: ["main"],
      Wrap: ({ children }) => (
        <StrictMode>
          <LazyMotion
            features={() => import("framer-motion").then(res => res.domMin)}
            strict
          >
            <Suspense fallback={null}>
              <DynamicToaster />
            </Suspense>
            <Provider store={store}>{children}</Provider>
          </LazyMotion>
        </StrictMode>
      ),
      InnerWrap: ({ children }) => <Providers>{children}</Providers>,
    });

    setupRouterSsrQueryIntegration({ router, queryClient: tempQueryClient });

    return router;
  });

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
